import { Injectable } from '@angular/core';
import { BehaviorSubject, interval, Observable, Subscription, switchMap, takeWhile } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { IUser } from '../models/User';
import { DynamicConfig } from './dynamic-config.service';
import { ConfirmComponent, IConfirmDialog } from '../../frontend/modals/confirm.component';
import { DialogService } from './dialog.service';

/*
  SessionStatusService is responsible to handle all the functionality related to
  session/inactivity timeout. It'll hit the /session-status endpoint.
 */

const DEFAULT_INTERVAL = 120;

@Injectable({
  providedIn: 'root'
})
export class SessionStatusService {
  isPolling: boolean = false;
  canPoll: boolean = false;
  pollingSubscription: Subscription;
  // We put the interval time in a BehaviorSubject so we can update it once we get
  // close to logging out so we're sure to get both the warning and log out toasts
  intervalInSeconds$: BehaviorSubject<number> = new BehaviorSubject<number>(DEFAULT_INTERVAL);

  constructor(
    private http: HttpClient,
    private router: Router,
    private toastService: ToastrService,
    public dialog: DialogService
  ) {}

  watchCurrentUser(currentUser$: Observable<IUser>) {
    currentUser$.subscribe((user) => {
      if (user?.id) {
        this.canPoll = true;
      } else {
        this.cancelPolling();
      }
    });
  }

  getStatusTimeout() {
    if (!this.isPolling && this.canPoll) {
      this.isPolling = true;
      // Watch for changes in the interval
      this.pollingSubscription = this.intervalInSeconds$
        .pipe(
          switchMap((time) => interval(time * 1000)),
          switchMap(() => this.http.get(`${environment.endpoints.sessionStatus}`, {})),
          takeWhile(() => this.canPoll && this.isPolling)
        )
        .subscribe({
          next: (res) => this.checkForSessionExpiration(res),
          error: (error) => this.handleError(error)
        });
    }
  }

  private checkForSessionExpiration(response: object) {
    const inactiveTime = Math.round(response['time_until_inactive']);
    const sessionExpiryTime = Math.round(response['time_until_session_expiry']);
    const currentInterval = this.intervalInSeconds$.value;
    let toastType = 'warning';
    let toastMessage = '';
    let updatedInterval;

    // Increase the threshold by 25% to make sure we capture the warning and leave time
    // for the user to be active again
    if (inactiveTime <= DEFAULT_INTERVAL * 1.25) {
      toastMessage = 'You will be logged out soon due to inactivity.';
      updatedInterval = inactiveTime - 9;
    }

    if (sessionExpiryTime < 300) {
      toastMessage =
        'Your session will expire soon and you will be logged out automatically. Please logout and log back in to renew.';

      // The session expiry threshold is larger so we only want to change the
      // interval time here if it's below the default (plus 25% like above) and the
      // updatedInterval hasn't been set or is less than the session expiry time
      if (sessionExpiryTime <= DEFAULT_INTERVAL * 1.25 && (!updatedInterval || updatedInterval > sessionExpiryTime)) {
        updatedInterval = sessionExpiryTime - 9;
      }
    }

    // If the user has reset the inactivity timer, we need to change the
    // polling interval back to the default.
    // (If updatedInterval is undefined, that means both inactiveTime and sessionExpiryTime
    // are greater than the default threshold)
    if (!updatedInterval && currentInterval < DEFAULT_INTERVAL) {
      updatedInterval = DEFAULT_INTERVAL;
    }

    // Update interval time
    if (updatedInterval && updatedInterval > 0) {
      this.changeInterval(updatedInterval);
    }

    if (inactiveTime < 10) {
      toastType = 'error';
      toastMessage = 'Your total session has been timed out due to inactivity.';
    }

    if (sessionExpiryTime < 10) {
      toastType = 'error';
      toastMessage = 'Your session has expired. Please login again.';
    }

    if (!toastMessage || (toastType === 'warning' && this.toastService.currentlyActive)) {
      return;
    }

    if (toastType === 'error') {
      this.logout(this.router.routerState.snapshot.url);
    }

    this.showToast(toastType, toastMessage);
    if (toastType === 'warning' && toastMessage) {
      this.showModal();
    }
  }

  private changeInterval(seconds: number) {
    this.intervalInSeconds$.next(seconds);
  }

  private handleError(error) {
    // The interceptor will log us out automatically so we just need to show the error here.
    if (error.statusText === 'Unauthorized') {
      this.showToast('error', 'Your session has expired. Please login again.');
    }
  }

  showToast(toastType: string, toastMessage: string) {
    this.toastService[toastType](toastMessage, '', {
      closeButton: true,
      timeOut: 0,
      extendedTimeOut: 0
    });
  }

  showModal() {
    this.dialog
      .open(ConfirmComponent, {
        data: <IConfirmDialog>{
          title: 'Session Expiring Soon',
          subTitle: `Fo your security, your session is about to expire due to inactivity or staying on this page for too long. To continue your session, please click Stay Signed in below`,
          confirmButtonText: 'Stay Signed in',
          closeButtonText: 'Sign out'
        }
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.keepSessionAlive(true);
          this.toastService.clear();
        } else {
          this.logout(this.router.routerState.snapshot.url);
        }
      });
  }

  private logout(next: string) {
    this.toastService.clear();
    this.dialog.closeAll();
    this.router.navigate(['/logout'], { queryParams: { next, timeout: 'true' } });
  }

  private cancelPolling() {
    this.pollingSubscription?.unsubscribe();
    this.isPolling = false;
    this.canPoll = false;
  }

  public keepSessionAlive(alive) {
    if (alive) {
      this.http.get<DynamicConfig>(environment.endpoints.dynamicConfig).subscribe(() => {});
    }
  }
}
